import { observer } from 'mobx-react-lite'
import { ContentError } from '@/components/ContentError'
import { ContentEmpty } from '@/components/ContentLayout'
import { NoResultsEmptyState } from '@/components/NoResultsEmptyState'
import { SkeletonList } from '@/components/Skeleton'
import { useFeatures } from '@/contexts/root-store-context'
import { useDocumentTitle } from '@/hooks/document-title'
import { useActivation } from '@/hooks/view-model-hooks'
import { WorkspaceSelectorViewHeadingContainer } from '@/features/iam/workspaces/components/WorkspaceSelectorViewHeading/WorkspaceSelectorViewHeadingContainer'
import {
  LegendGrid,
  LegendList,
  LegendListEmpty,
} from '@/features/legends/LegendList'
import { LegendCardSkeleton } from '@/features/legends/LegendList/components/LegendCard'
import { LegendCardContainer } from '@/features/legends/LegendList/components/LegendCard/LegendCardContainer'
import { NewLegendDialogContainer } from '@/features/legends/NewLegendDialog/NewLegendDialogContainer'
import type { LegendListViewModel } from './LegendListViewModel'

const testId = 'legend-list'

export default observer(function LegendListContainer() {
  const { legends } = useFeatures((r) => ({
    legends: r.legends.internal(),
  }))
  const vm = legends.legendListViewModel
  useDocumentTitle('Legends')
  useActivation(vm)

  const workspaceSelectorVm = vm.workspaceSelector
  return (
    <LegendList
      heading={
        <WorkspaceSelectorViewHeadingContainer
          primaryText="Legends"
          dropdownTitle="Select workspace"
          allOptionText={null}
          workspaces={workspaceSelectorVm.filteredWorkspaces}
          value={workspaceSelectorVm.selectedWorkspace}
          onChange={workspaceSelectorVm.selectWorkspace}
          searcher={workspaceSelectorVm}
          alwaysUseDropdown={workspaceSelectorVm.workspaces.length > 1}
        />
      }
      canCreateLegends={vm.canCreateLegends}
      onCreateLegend={vm.newLegendVm.modal.show}
      filter={vm.filter}
      onFilterChange={vm.setFilter}
      inactiveCount={vm.inactiveCount}
      searchText={vm.searchText}
      onSearchTextChange={vm.setSearchText}
      count={vm.items.length}
      isSkeleton={vm.isFetchingInitial}
      dialogs={<NewLegendDialogContainer vm={vm.newLegendVm} />}
      testId={testId}
    >
      <LegendListContentContainer vm={vm} />
    </LegendList>
  )
})

interface LegendListContentContainerProps {
  vm: LegendListViewModel
}

const LegendListContentContainer = observer(
  function LegendListContentContainer({ vm }: LegendListContentContainerProps) {
    return vm.fetch.match({
      pending: () =>
        vm.isFetchingInitial ? (
          <LegendGrid>
            <SkeletonList count={6}>
              <LegendCardSkeleton />
            </SkeletonList>
          </LegendGrid>
        ) : (
          <LegendListItemsContainer vm={vm} />
        ),
      rejected: (err) => (
        <ContentError
          title="Loading the legends failed"
          error={err}
          onRetry={vm.retry}
          size="section"
        />
      ),
      resolved: () => <LegendListItemsContainer vm={vm} />,
    })
  },
)

const LegendListItemsContainer = observer(function LegendListItemsContainer({
  vm,
}: LegendListContentContainerProps) {
  const { shellInfo } = useFeatures(({ shell }) => ({
    shellInfo: shell.shellInfo,
  }))
  return vm.isEmpty ? (
    <ContentEmpty>
      <LegendListEmpty
        canCreateLegends={vm.canCreateLegends}
        onCreateLegend={vm.newLegendVm.modal.show}
        testId={`${testId}-empty`}
      />
    </ContentEmpty>
  ) : vm.items.length === 0 ? (
    <NoResultsEmptyState
      corporateMode={shellInfo.corporateMode}
      isSearchFilterApplied={vm.isSearchFilterApplied}
      onClearSearchFilter={vm.clearSearchText}
    />
  ) : (
    <LegendGrid>
      {vm.items.map((itemVm) => (
        <LegendCardContainer key={itemVm.legend.id} vm={itemVm} />
      ))}
    </LegendGrid>
  )
})
